const API_BASE_URL = (
  typeof window !== 'undefined'
    ? ''
    : process.env.NEXT_PUBLIC_API_URL || process.env.API_PROXY_TARGET || ''
).replace(/\/$/, '');


export class ApiError extends Error {
  status: number;
  details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

// Suppress unhandled rejections and console errors for expected 401 session expirations
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    if (
      reason?.status === 401 ||
      reason?.message?.includes?.('Authentication token has expired') ||
      reason?.message?.includes?.('Session expired') ||
      reason?.message?.includes?.('jwt expired') ||
      reason?.message?.includes?.('Unauthorized')
    ) {
      event.preventDefault();
    }
  });

  if (!(window as any).__dfms_api_console_patched) {
    (window as any).__dfms_api_console_patched = true;
    const origError = console.error;
    console.error = function (...args: any[]) {
      const first = args[0];
      const msg = first ? String(first?.message || first) : '';
      if (
        msg.includes('Authentication token has expired') ||
        msg.includes('Session expired') ||
        msg.includes('jwt expired') ||
        first?.status === 401
      ) {
        return;
      }
      origError.apply(console, args);
    };
  }
}

export type ApiPagination = {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
};

type ApiSuccess<T> = {
  success: true;
  message?: string;
  data?: T;
  pagination?: ApiPagination;
};

type ApiFailure = {
  success: false;
  message?: string;
  details?: unknown;
};

/** Per-tab force-login JWT (sessionStorage — not shared across tabs). */
const TAB_ACCESS_TOKEN_KEY = 'flipbook_tab_access_token';
const ACTIVE_ORGANIZATION_KEY = 'selected_organization_id';

export function getTabAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(TAB_ACCESS_TOKEN_KEY);
}

export function saveTabAccessToken(token: string) {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(TAB_ACCESS_TOKEN_KEY, token);
}

export function clearTabAccessToken() {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(TAB_ACCESS_TOKEN_KEY);
}

export function hasTabScopedSession(): boolean {
  return Boolean(getTabAccessToken());
}

export function getActiveOrganizationId(): string | null {
  if (typeof window === 'undefined') return null;
  const isTabScoped = hasTabScopedSession();
  if (isTabScoped) {
    return sessionStorage.getItem(ACTIVE_ORGANIZATION_KEY);
  }
  return (
    sessionStorage.getItem(ACTIVE_ORGANIZATION_KEY) ||
    localStorage.getItem(ACTIVE_ORGANIZATION_KEY)
  );
}

export function saveActiveOrganizationId(orgId: string | number | 'all') {
  if (typeof window === 'undefined') return;
  const val = String(orgId);
  sessionStorage.setItem(ACTIVE_ORGANIZATION_KEY, val);
  if (!hasTabScopedSession()) {
    localStorage.setItem(ACTIVE_ORGANIZATION_KEY, val);
  }
}

export function clearActiveOrganizationId() {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(ACTIVE_ORGANIZATION_KEY);
  if (!hasTabScopedSession()) {
    localStorage.removeItem(ACTIVE_ORGANIZATION_KEY);
  }
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiSuccess<T>> {
  const headers = new Headers(options.headers || {});

  if (
    options.body &&
    !headers.has('Content-Type') &&
    !(options.body instanceof FormData)
  ) {
    headers.set('Content-Type', 'application/json');
  }

  // Prefer tab-scoped Bearer token so force-login does not depend on (or
  // overwrite) the shared httpOnly cookie used by other tabs.
  const tabToken = getTabAccessToken();
  if (tabToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${tabToken}`);
  }

  // Inject current organization header if active
  const activeOrgId = getActiveOrganizationId();
  if (activeOrgId && !headers.has('x-organization-id')) {
    headers.set('x-organization-id', activeOrgId);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  console.log(`[API] ${options.method || 'GET'} ${API_BASE_URL}${path} -> ${response.status}`);

  let payload: ApiSuccess<T> | ApiFailure | null = null;

  try {
    payload = (await response.json()) as ApiSuccess<T> | ApiFailure;
  } catch {
    payload = null;
  }

  if (!response.ok || !payload || payload.success === false) {
    console.warn(`[API Error] ${options.method || 'GET'} ${path} failed (${response.status}):`, payload?.message);
    if (
      typeof window !== 'undefined' &&
      response.status === 401 &&
      !path.includes('/api/auth/login') &&
      !path.includes('/api/auth/forgot-password') &&
      !path.includes('/api/auth/verify-otp') &&
      !path.includes('/api/auth/reset-password') &&
      !path.includes('/api/auth/verify-2fa') &&
      !path.includes('/api/auth/resend-2fa')
    ) {
      console.warn(`[API] Dispatching session-expired event for 401 on ${path}`);
      window.dispatchEvent(
        new CustomEvent('flipbook:session-expired', {
          detail: { path, message: payload?.message || 'Session expired' },
        })
      );
    }

    throw new ApiError(
      payload?.message || `Request failed (${response.status})`,
      response.status,
      payload && 'details' in payload ? payload.details : undefined
    );
  }

  return payload;
}

export { API_BASE_URL };
